import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/api/client";
import TopBar from "@/components/TopBar";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Trash2, Upload, Paperclip, Pencil, X, Download } from "lucide-react";
import { receiptUrl } from "@/utils/receipt";

const today = () => new Date().toISOString().slice(0, 10);
const CURRENT = { year: new Date().getFullYear(), month: new Date().getMonth() + 1 };

export default function ExpenseForm() {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const editIdFromUrl = params.get("edit");

  const [editingId, setEditingId] = useState(editIdFromUrl || null);
  const [date, setDate] = useState(today());
  const [category, setCategory] = useState("fuel");
  const [categories, setCategories] = useState([]);
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [file, setFile] = useState(null);
  const [items, setItems] = useState([]);
  const [msg, setMsg] = useState(""); const [err, setErr] = useState("");

  // Item 6: Only show CURRENT-month rows + any past returned/rejected that still need attention.
  const reload = () =>
    api.get(`/expenses/me?year=${CURRENT.year}&month=${CURRENT.month}&include_returned_all=true`)
      .then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []);

  useEffect(() => {
    api.get("/expense-categories").then((r) => {
      setCategories(r.data);
      if (r.data.length > 0 && !r.data.find((c) => c.code === category)) {
        setCategory(r.data[0].code);
      }
    }).catch(() => setCategories([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const catLabel = (code) => {
    const c = categories.find((x) => x.code === code);
    if (c) return i18n.language === "en" ? c.label_en : c.label_ja;
    return code;
  };

  // If ?edit=<id> present, prefill form from that item after items load
  useEffect(() => {
    if (editIdFromUrl && items.length) {
      const found = items.find((e) => e.id === editIdFromUrl);
      if (found && found.status !== "approved") {
        startEdit(found);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, editIdFromUrl]);

  const editingItem = items.find((e) => e.id === editingId);

  const startEdit = (e) => {
    setEditingId(e.id);
    setDate(e.expense_date);
    setCategory(e.category);
    setDescription(e.description || "");
    setAmount(String(e.amount));
    setFile(null);
    const el = document.getElementById("exp-create-file"); if (el) el.value = "";
    setMsg(""); setErr("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDate(today()); setCategory("fuel"); setDescription(""); setAmount(""); setFile(null);
    const el = document.getElementById("exp-create-file"); if (el) el.value = "";
    setMsg(""); setErr("");
    if (editIdFromUrl) setParams({});
  };

  const uploadReceipt = async (expenseId, f) => {
    const fd = new FormData();
    fd.append("file", f);
    await api.post(`/receipts/upload/${expenseId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  };

  const save = async (asSubmit) => {
    setErr(""); setMsg("");
    if (!amount || Number(amount) <= 0) { setErr("金額を入力してください"); return; }
    try {
      let data;
      if (editingId) {
        const res = await api.patch(`/expenses/${editingId}`, {
          expense_date: date, category, description, amount: Number(amount), submit: asSubmit,
        });
        data = res.data;
      } else {
        const res = await api.post("/expenses", {
          expense_date: date, category, description, amount: Number(amount), submit: asSubmit,
        });
        data = res.data;
      }
      if (file && data?.id) {
        try { await uploadReceipt(data.id, file); }
        catch (upErr) { setErr("経費は保存しましたが領収書のアップロードに失敗しました: " + (formatApiErrorDetail(upErr.response?.data?.detail) || upErr.message)); }
      }
      // Bug fix (iter17): cancelEdit() resets msg to "" so we must call it
      // BEFORE setting the success banner, otherwise the toast never renders.
      const successMsg = editingId
        ? (asSubmit ? t("driver.resubmitted") : t("driver.saved"))
        : (asSubmit ? "提出しました" : "下書きを保存しました");
      cancelEdit();
      setMsg(successMsg);
      setTimeout(() => setMsg(""), 3500);
      reload();
      return data;
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const uploadExtra = async (expenseId, f) => {
    try { await uploadReceipt(expenseId, f); reload(); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("削除しますか？")) return;
    await api.delete(`/expenses/${id}`);
    if (editingId === id) cancelEdit();
    reload();
  };

  const submitItem = async (id) => {
    await api.patch(`/expenses/${id}`, { submit: true });
    reload();
  };

  const delReceipt = async (rid) => {
    await api.delete(`/receipts/${rid}`);
    reload();
  };

  const isReturned = editingItem?.status === "returned";
  const submitLabel = editingId
    ? (isReturned ? t("driver.editAndResubmit") : (editingItem?.status === "draft" ? t("driver.submitExpense") : t("driver.updateAndSubmit")))
    : t("driver.submitExpense");

  return (
    <div className="shell">
      <TopBar />
      <div className="container">
        <button className="btn btn-outline" onClick={() => nav(-1)} style={{ marginBottom: 12 }}>← {t("common.back")}</button>
        <h1 className="page-title">{editingId ? t("driver.editingExpense") : t("driver.registerExpense")}</h1>

        {editingId && editingItem && (
          <div data-testid="exp-edit-banner" className={`banner banner-${editingItem.status}`} style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Pencil size={14} />
                <strong>{t("driver.editingRecord")}</strong>
                <span className={`tag tag-${editingItem.status}`}>{t(`common.${editingItem.status}`)}</span>
              </div>
              <button className="btn btn-outline" style={{ padding: "4px 10px", fontSize: 12 }} onClick={cancelEdit} data-testid="exp-cancel-edit">
                <X size={12} /> {t("common.cancel")}
              </button>
            </div>
            {editingItem.review_note && (
              <div style={{ marginTop: 8, fontSize: 13, color: "#334155", background: "#fff", padding: 8, borderRadius: 6 }}>
                <strong>{t("common.note")}:</strong> {editingItem.review_note}
              </div>
            )}
          </div>
        )}

        <div className="card" style={{ marginBottom: 20 }}><div className="card-body">
          <div className="grid grid-2">
            <div className="field">
              <label className="label">{t("common.date")}</label>
              <input data-testid="exp-date" type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="field">
              <label className="label">{t("driver.category")}</label>
              <select data-testid="exp-category" className="select" value={category} onChange={(e) => setCategory(e.target.value)}>
                {categories.map((c) => (
                  <option key={c.id} value={c.code}>{i18n.language === "en" ? c.label_en : c.label_ja}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-2">
            <div className="field">
              <label className="label">{t("driver.amount")} (¥)</label>
              <input data-testid="exp-amount" type="number" min="0" className="input" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div className="field">
              <label className="label">{t("driver.description")}</label>
              <input data-testid="exp-description" className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
          </div>
          <div className="field">
            <label className="label">{t("driver.uploadReceipt")}</label>
            <label className="btn btn-outline" style={{ cursor: "pointer", justifyContent: "flex-start" }}>
              <Paperclip size={14} />
              <span style={{ marginLeft: 6 }}>{file ? file.name : (editingId ? "追加ファイルを選択（任意）" : "ファイルを選択（画像 / PDF, 最大 15MB）")}</span>
              <input
                id="exp-create-file"
                data-testid="exp-create-file"
                type="file"
                accept="image/*,application/pdf,.heic,.heif"
                style={{ display: "none" }}
                onChange={(ev) => setFile(ev.target.files[0] || null)}
              />
            </label>
          </div>
          {err && <div style={{ color: "#DC2626", marginBottom: 10 }} data-testid="exp-err">{err}</div>}
          {msg && (
            <div data-testid="exp-msg" style={{
              background: "#DCFCE7", border: "1px solid #16A34A", color: "#166534",
              padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontWeight: 600,
            }}>
              ✅ {msg}
            </div>
          )}
          <div className="row-actions">
            {editingId && <button className="btn btn-outline" onClick={cancelEdit} data-testid="exp-cancel-edit-bottom">{t("common.cancel")}</button>}
            <button data-testid="exp-save-draft" className="btn btn-outline" onClick={() => save(false)}>
              {editingId ? t("driver.saveChanges") : t("driver.saveDraft")}
            </button>
            <button data-testid="exp-submit" className={isReturned ? "btn btn-warning" : "btn btn-primary"} onClick={() => save(true)}>
              {submitLabel}
            </button>
          </div>
        </div></div>

        <h2 className="page-title" style={{ fontSize: 18 }}>{t("driver.myExpenses")}</h2>
        <div className="card"><div className="card-body table-wrap">
          <table className="table" data-testid="exp-list">
            <thead><tr>
              <th>{t("common.date")}</th><th>{t("driver.category")}</th><th>{t("driver.description")}</th>
              <th>{t("driver.amount")}</th><th>{t("common.status")}</th><th>{t("driver.uploadReceipt")}</th><th>{t("common.actions")}</th>
            </tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="7" style={{ textAlign: "center", color: "#64748B" }}>{t("driver.noRecords")}</td></tr>}
              {items.map((e) => (
                <tr key={e.id} style={editingId === e.id ? { background: "#FEF3C7" } : {}}>
                  <td>{e.expense_date}</td>
                  <td>{catLabel(e.category)}</td>
                  <td>{e.description}</td>
                  <td>¥{Number(e.amount).toLocaleString()}</td>
                  <td>
                    <span className={`tag tag-${e.status}`}>{t(`common.${e.status}`)}</span>
                    {e.review_note && (e.status === "rejected" || e.status === "returned") && (
                      <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>{e.review_note}</div>
                    )}
                  </td>
                  <td>
                    <div>
                      {(e.receipts || []).map((r) => (
                        <span key={r.id} className="receipt-thumb" style={{ display: "inline-flex", gap: 4, alignItems: "center", marginRight: 6 }}>
                          <a href={receiptUrl(r.file_url)} target="_blank" rel="noreferrer" data-testid={`exp-receipt-open-${r.id}`}>{r.original_name}</a>
                          <a href={receiptUrl(r.file_url)} download={r.original_name || "receipt"} className="btn btn-outline" style={{ padding: "2px 6px", fontSize: 11 }} data-testid={`exp-receipt-download-${r.id}`} title="ダウンロード">
                            <Download size={11} />
                          </a>
                          {e.status !== "approved" && <button onClick={() => delReceipt(r.id)} className="btn btn-danger" style={{ padding: "2px 6px", fontSize: 11 }}>×</button>}
                        </span>
                      ))}
                      {e.status !== "approved" && (
                        <label className="btn btn-outline" style={{ cursor: "pointer", fontSize: 12, padding: "6px 10px" }}>
                          <Upload size={12} /> 追加
                          <input data-testid={`upload-${e.id}`} type="file" accept="image/*,application/pdf,.heic,.heif" style={{ display: "none" }}
                            onChange={(ev) => ev.target.files[0] && uploadExtra(e.id, ev.target.files[0])} />
                        </label>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="row-actions">
                      {e.status !== "approved" && (
                        <button
                          className={e.status === "returned" ? "btn btn-warning" : "btn btn-outline"}
                          style={{ padding: "6px 10px", fontSize: 12 }}
                          onClick={() => startEdit(e)}
                          data-testid={`exp-edit-${e.id}`}
                          title={e.status === "returned" ? t("driver.editAndResubmit") : t("common.edit")}
                        >
                          <Pencil size={12} /> {e.status === "returned" ? t("driver.editAndResubmit") : t("common.edit")}
                        </button>
                      )}
                      {e.status === "draft" && (
                        <button className="btn btn-primary" style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => submitItem(e.id)}>{t("common.submit")}</button>
                      )}
                      {e.status !== "approved" && (
                        <button className="btn btn-danger" style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => del(e.id)}><Trash2 size={12} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div></div>
      </div>
    </div>
  );
}
